import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { getDocsByIdsChunked } from '../utils/firestoreChunking';
import { formatShortDate } from '../utils/dateFormat';
import welcomePhoto from '../assets/welcomePhoto.png';
import './Dashboard.css';
import './reviewHistory.css';

const StarDisplay = ({ value, max = 5 }) => (
  <span className="star-display">
    {Array.from({ length: max }, (_, i) => (
      <span key={i} className={i < Math.round(value) ? 'star filled' : 'star'}>&#9733;</span>
    ))}
    <span className="star-value">{value.toFixed(1)}</span>
  </span>
);

const ReviewCard = ({ review, navigate }) => {
  const mealTitle = review.meal?.title || 'Meal';
  const mealDate = formatShortDate(review.meal?.dateTime);

  if (!review.isVisible) {
    return (
      <div className="review-card review-card--locked">
        <div className="review-card-meal">
          <span className="review-meal-title">{mealTitle}</span>
          {mealDate && <span className="review-meal-date">{mealDate}</span>}
        </div>
        <div className="review-locked-body">
          <span className="review-lock-icon">&#128274;</span>
          <p className="review-locked-message">
            Leave your review to unlock this one.
          </p>
          <button
            className="review-unlock-button"
            onClick={() => navigate(`/create-review/${review.bookingId}`)}
          >
            Leave a Review
          </button>
        </div>
      </div>
    );
  }

  const reviewerName = review.reviewer
    ? [review.reviewer.userProfile?.firstName, review.reviewer.userProfile?.lastName]
        .filter(Boolean).join(' ') || 'Anonymous'
    : 'Anonymous';

  const reviewerImage = review.reviewer?.userProfile?.profileImage;

  return (
    <div className="review-card review-card--visible">
      <div className="review-card-meal">
        <span className="review-meal-title">{mealTitle}</span>
        {mealDate && <span className="review-meal-date">{mealDate}</span>}
      </div>
      <div className="review-card-reviewer">
        {reviewerImage ? (
          <img src={reviewerImage} alt={reviewerName} className="review-avatar" />
        ) : (
          <div className="review-avatar review-avatar--placeholder">
            {reviewerName.charAt(0).toUpperCase()}
          </div>
        )}
        <span className="review-reviewer-name">{reviewerName}</span>
      </div>
      <div className="review-ratings">
        <div className="review-rating-row">
          <span className="review-rating-label">Overall</span>
          <StarDisplay value={review.overallRating} />
        </div>
        <div className="review-rating-row">
          <span className="review-rating-label">
            {review.reviewerRole === 'guest' ? 'Food Quality' : 'Guest Conduct'}
          </span>
          <StarDisplay value={review.culinaryRating} />
        </div>
        <div className="review-rating-row">
          <span className="review-rating-label">Communication</span>
          <StarDisplay value={review.serviceRating} />
        </div>
        <div className="review-rating-row">
          <span className="review-rating-label">
            {review.reviewerRole === 'guest' ? 'Cleanliness' : 'Respectfulness'}
          </span>
          <StarDisplay value={review.cleanlinessRating} />
        </div>
      </div>
      {review.reviewText && (
        <p className="review-text">&#8220;{review.reviewText}&#8221;</p>
      )}
    </div>
  );
};

const Reviews = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('host');
  const [asHostReviews, setAsHostReviews] = useState([]);
  const [asGuestReviews, setAsGuestReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!currentUser) return;

      try {
        // Reviews received as host (guests reviewing the host)
        const hostReceivedSnap = await getDocs(query(
          collection(db, 'reviews'),
          where('hostId', '==', currentUser.uid),
          where('reviewerRole', '==', 'guest')
        ));

        // Reviews received as guest (hosts reviewing the guest)
        const guestReceivedSnap = await getDocs(query(
          collection(db, 'reviews'),
          where('guestId', '==', currentUser.uid),
          where('reviewerRole', '==', 'host')
        ));

        const hostReviews = hostReceivedSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const guestReviews = guestReceivedSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Collect all unique meal IDs to fetch
        const allMealIds = [...new Set([
          ...hostReviews.map(r => r.mealId),
          ...guestReviews.map(r => r.mealId),
        ].filter(Boolean))];

        // Collect reviewer IDs only for visible reviews
        const visibleHostReviews = hostReviews.filter(r => r.isVisible);
        const visibleGuestReviews = guestReviews.filter(r => r.isVisible);
        const reviewerIds = [...new Set([
          ...visibleHostReviews.map(r => r.guestId),
          ...visibleGuestReviews.map(r => r.hostId),
        ].filter(Boolean))];

        // Batch fetch meals and reviewers in parallel
        const [mealDocs, reviewerDocs] = await Promise.all([
          getDocsByIdsChunked(collection(db, 'meals'), allMealIds),
          getDocsByIdsChunked(collection(db, 'users'), reviewerIds),
        ]);

        const mealMap = {};
        mealDocs.forEach(d => { mealMap[d.id] = d.data(); });

        const reviewerMap = {};
        reviewerDocs.forEach(d => { reviewerMap[d.id] = d.data(); });

        const enrichHostReviews = hostReviews.map(r => ({
          ...r,
          meal: mealMap[r.mealId] || null,
          reviewer: r.isVisible ? (reviewerMap[r.guestId] || null) : null,
        })).sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() ?? 0;
          const bTime = b.createdAt?.toMillis?.() ?? 0;
          return bTime - aTime;
        });

        const enrichGuestReviews = guestReviews.map(r => ({
          ...r,
          meal: mealMap[r.mealId] || null,
          reviewer: r.isVisible ? (reviewerMap[r.hostId] || null) : null,
        })).sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() ?? 0;
          const bTime = b.createdAt?.toMillis?.() ?? 0;
          return bTime - aTime;
        });

        setAsHostReviews(enrichHostReviews);
        setAsGuestReviews(enrichGuestReviews);
      } catch (err) {
        console.error('Error fetching reviews:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [currentUser]);

  if (!currentUser || loading) {
    return (
      <div className="dashboard-container">
        <p className="dashboard-loading">Loading...</p>
      </div>
    );
  }

  const activeReviews = tab === 'host' ? asHostReviews : asGuestReviews;

  return (
    <div className="dashboard-container">
      <img src={welcomePhoto} alt="Welcome" className="dashboard-image" />

      <div className="dashboard-card">
        <div className="meals-tile">
          <h2 className="meals-tile-title">My Reviews</h2>

          <div className="meals-toggle">
            <button
              className={`meals-toggle-btn ${tab === 'host' ? 'active' : ''}`}
              onClick={() => setTab('host')}
            >
              As Host
            </button>
            <button
              className={`meals-toggle-btn ${tab === 'guest' ? 'active' : ''}`}
              onClick={() => setTab('guest')}
            >
              As Guest
            </button>
          </div>

          {activeReviews.length === 0 ? (
            <p className="meals-empty">
              {tab === 'host'
                ? 'No reviews yet as a host.'
                : 'No reviews yet as a guest.'}
            </p>
          ) : (
            <div className="reviews-list">
              {activeReviews.map(review => (
                <ReviewCard key={review.id} review={review} navigate={navigate} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reviews;
